import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useJoinSession, useTelemedicineSessionByAppointment } from '@/hooks/telemedicine';
import { Button } from '@/components/ui/Button';
import { ShieldCheck } from 'lucide-react';

export function WaitingRoomPage() {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { user } = useAuth();
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);
  const [cameraTest, setCameraTest] = useState<'PENDING' | 'PASS' | 'FAIL'>('PASS');
  const [micTest, setMicTest] = useState<'PENDING' | 'PASS' | 'FAIL'>('PASS');

  const { data: session } = useTelemedicineSessionByAppointment(appointmentId);
  const { mutateAsync: joinSession, isPending } = useJoinSession();

  useEffect(() => {
    navigator.mediaDevices?.getUserMedia({ video: true, audio: true })
      .then(stream => {
        setCameraTest('PASS');
        setMicTest('PASS');
        stream.getTracks().forEach(track => track.stop());
      })
      .catch(() => {
        // Do not block user if device permission is rejected or no camera
      });
  }, []);

  const handleJoin = async () => {
    const actualUserId = user?.id || (user as any)?.userId;
    const actualSessionId = session?.id || appointmentId;

    console.info('[WAITING-ROOM] handleJoin clicado!', {
      actualUserId,
      actualSessionId,
      session,
      appointmentId,
      lgpdConsent,
      isPending,
    });

    if (!actualUserId || !actualSessionId) {
      console.warn('[WAITING-ROOM] Bloqueado: faltando userId ou sessionId/appointmentId');
      return;
    }

    try {
      if (session?.id) {
        await joinSession({
          sessionId: session.id,
          userId: actualUserId,
          role: 'PATIENT',
        });
      }
    } catch (joinErr) {
      console.warn('[WAITING-ROOM] Aviso ao registrar joinSession (navegando para a sala de video):', joinErr);
    }

    navigate(`/telemedicine/video/${actualSessionId}`);
  };

  return (
    <div className="container max-w-3xl py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sala de Espera</h1>
        <p className="text-gray-500 mt-1">
          Teste seu equipamento antes de entrar na consulta
        </p>
      </div>

      {session ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p className="text-sm text-emerald-900">
              <strong>Sessão confirmada.</strong> Seu médico já está disponível na sala.
            </p>
          </div>

          <ChecklistItem
            label="Câmera"
            state={cameraTest}
            onTest={async () => {
              setCameraTest('PENDING');
              try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                stream.getTracks().forEach(track => track.stop());
                setCameraTest('PASS');
              } catch {
                setCameraTest('FAIL');
              }
            }}
          />

          <ChecklistItem
            label="Microfone"
            state={micTest}
            onTest={async () => {
              setMicTest('PENDING');
              try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                stream.getTracks().forEach(track => track.stop());
                setMicTest('PASS');
              } catch {
                setMicTest('FAIL');
              }
            }}
          />

          <ChecklistItem
            label="Rede"
            state="PASS"
            sublabel="Conexão detectada"
          />

          {/* Termo de Consentimento Livre e Esclarecido (TCLE / LGPD / CFM) */}
          <div className="rounded-xl border border-blue-200/80 bg-blue-50/50 p-4.5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-900 font-bold text-sm">
                <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
                <span>Termo de Consentimento para Telemedicina e Gravação Criptografada</span>
              </div>
              <button
                type="button"
                onClick={() => setShowFullTerms(!showFullTerms)}
                className="text-xs font-semibold text-blue-700 hover:text-blue-900 underline cursor-pointer"
              >
                {showFullTerms ? 'Ocultar detalhes' : 'Ler termo completo'}
              </button>
            </div>

            <p className="text-xs text-blue-950/80 leading-relaxed">
              Em conformidade com a <strong>Resolução CFM nº 2.314/2022</strong> e a <strong>Lei Geral de Proteção de Dados (LGPD nº 13.709/2018)</strong>, 
              sua teleconsulta médica é realizada em canal protegido com criptografia de ponta a ponta (AES-256). 
              A sessão poderá ter registro audiovisual gravado e integrado ao seu prontuário médico eletrônico estritamente sob sigilo profissional, 
              para respaldo do diagnóstico, segurança clínica do paciente e cumprimento de obrigações legais do médico.
            </p>

            {showFullTerms && (
              <div className="text-[11px] text-slate-700 bg-white/90 rounded-lg p-3 border border-blue-200 space-y-2 max-h-48 overflow-y-auto leading-normal">
                <p><strong>1. Natureza do Atendimento:</strong> A teleconsulta é um ato médico realizado à distância, sujeito às limitações inerentes à impossibilidade de exame físico presencial direto. O profissional poderá recomendar atendimento presencial a qualquer momento se julgar clinicamente necessário.</p>
                <p><strong>2. Gravação e Sigilo Profissional:</strong> O registro audiovisual da consulta, quando ativado, tem finalidade exclusiva de auditoria clínica, continuidade assistencial e cumprimento do dever de guarda de prontuário, sendo inviolável e acessível apenas pelo médico e pelo próprio paciente.</p>
                <p><strong>3. Armazenamento e Criptografia:</strong> Todo o tráfego de dados, áudio, vídeo e documentos compartilhados durante a chamada é cifrado ponta a ponta com padrões de segurança em nível bancário e hospitalar, em servidores seguros dedicados à saúde.</p>
                <p><strong>4. Direitos do Titular:</strong> O paciente tem direito de solicitar acesso, esclarecimentos e exercer seus direitos garantidos pela LGPD mediante os canais oficiais da plataforma Medikei Saúde.</p>
              </div>
            )}

            <label className="flex items-start gap-3 p-3 bg-white border border-blue-200 rounded-lg cursor-pointer transition-colors hover:bg-blue-50/40">
              <input
                type="checkbox"
                checked={lgpdConsent}
                onChange={e => setLgpdConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary shrink-0"
              />
              <span className="text-xs sm:text-sm text-gray-900 font-medium leading-relaxed">
                Li, compreendi e <strong>concordo expressamente</strong> com os termos da teleconsulta, autorizando a realização do atendimento online e o registro audiovisual criptografado nos termos da legislação médica e da LGPD.
              </span>
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => navigate(-1)}>Voltar</Button>
            <Button
              onClick={handleJoin}
              disabled={!lgpdConsent || isPending}
              className="font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? 'Entrando na sala...' : 'Concordar e Entrar na Consulta'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500">
          Carregando informações da sessão...
        </div>
      )}
    </div>
  );
}

function ChecklistItem({
  label,
  state,
  sublabel,
  onTest,
}: {
  label: string;
  state: 'PENDING' | 'PASS' | 'FAIL';
  sublabel?: string;
  onTest?: () => void;
}) {
  const stateColor = {
    PENDING: 'bg-gray-300',
    PASS: 'bg-green-500',
    FAIL: 'bg-red-500',
  }[state];
  const stateLabel = {
    PENDING: 'Não testado',
    PASS: '✓ OK',
    FAIL: '✗ Falhou',
  }[state];

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <span className={`w-3 h-3 rounded-full ${stateColor}`} />
        <div>
          <div className="text-sm font-medium text-gray-900">{label}</div>
          {sublabel && <div className="text-xs text-gray-500">{sublabel}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">{stateLabel}</span>
        {onTest && (
          <button
            type="button"
            onClick={onTest}
            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            Testar
          </button>
        )}
      </div>
    </div>
  );
}